/**
 * Canonical Area options — single source of truth for every Area
 * dropdown in the app (Diario, Lavoro Quotidiano, Registro, Scadenze
 * and any future operational form).
 *
 * Why centralised?
 *   Until 20/02/2026 the array was duplicated in 4 separate tab files.
 *   Adding a new value ("Servizi Agenzia") required touching all of
 *   them and keeping the alphabetical order in sync by hand. This
 *   module makes drift between dropdowns impossible.
 *
 * Invariants:
 *   - Sorted ALPHABETICALLY (Italian locale).
 *   - The string `value` MUST match the canonical server-side
 *     `AREA_TYPES_ALLOWED` set in `backend/server.py` (also
 *     alphabetical there). Any change here MUST be mirrored on the
 *     backend, otherwise POST/PUT will return 400 "Area non valida".
 *
 * Usage:
 *   import { AREA_OPTIONS, AREA_VALUES } from "@/lib/areaOptions";
 *   {AREA_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
 */

export const AREA_OPTIONS = Object.freeze([
  { value: "Contabilità",     label: "Contabilità" },
  { value: "Lavoro",          label: "Lavoro" },
  // NB: `value` canonico resta "Servizi Agenzia" per non rompere i
  // dati storici e la whitelist backend `AREA_TYPES_ALLOWED`.
  // La label visibile è stata rinominata in "Servizi vari" su
  // richiesta utente (20/02/2026) perché ora copre anche servizi
  // non agenziali.
  { value: "Servizi Agenzia", label: "Servizi vari" },
]);

// Convenience array of just the canonical string values (handy for
// validations, filters and tests).
export const AREA_VALUES = Object.freeze(AREA_OPTIONS.map((o) => o.value));

/**
 * Restituisce la label "user-friendly" per un valore canonico Area.
 * Usare ovunque si mostri `caseItem.operationalArea` / `note.area` /
 * ecc. direttamente in UI, così la rinomina "Servizi Agenzia" →
 * "Servizi vari" è coerente con i dropdown senza migrare i dati
 * storici nel database.
 *
 *   getAreaLabel("Servizi Agenzia") → "Servizi vari"
 *   getAreaLabel("Lavoro")          → "Lavoro"
 *   getAreaLabel(undefined)         → ""
 */
export function getAreaLabel(value) {
  if (!value) return "";
  const found = AREA_OPTIONS.find((o) => o.value === value);
  return found ? found.label : value;
}
